import { Autocomplete, SxProps, TextField, Theme } from "@mui/material";
import { passwordManagerIgnoreInputProps } from "components/Forms/passwordManagerIgnoreInputProps";
import { STATES } from "encamp-shared/src/constants/states";
import { ReactNode } from "react";
import { Control, FieldValues, Path } from "react-hook-form";

import { SuggestedField } from "./SuggestedField";

type SuggestedStatePickerProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T>;
  needsReview: boolean;
  label?: string;
  sx?: SxProps<Theme>;
  /** Extra node rendered at the input's trailing edge, after the built-in adornments. */
  endAdornment?: ReactNode;
};

/**
 * Convenience wrapper around SuggestedField for picking a US state 2-letter code.
 * For plain TextFields, use SuggestedTextField.
 */
export function SuggestedStatePicker<T extends FieldValues>({
  name,
  control,
  needsReview,
  label = "State/Territory",
  sx,
  endAdornment,
}: SuggestedStatePickerProps<T>) {
  return (
    <SuggestedField name={name} control={control} needsReview={needsReview}>
      {({ field, isSuggested, sparkleAdornment }) => {
        const options: string[] = [...STATES];
        const stringValue = typeof field.value === "string" ? field.value : "";
        const isInvalidValue = stringValue !== "" && !options.includes(stringValue);
        return (
          <Autocomplete<string, false, false, true>
            options={options}
            value={stringValue || null}
            onChange={(_, value) => field.onChange(value ?? "")}
            onBlur={field.onBlur}
            freeSolo
            autoHighlight
            size="small"
            sx={sx}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                size="small"
                error={isInvalidValue}
                helperText={isInvalidValue ? "Not a valid state code" : undefined}
                inputProps={{
                  ...params.inputProps,
                  ...passwordManagerIgnoreInputProps,
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: isSuggested ? sparkleAdornment : params.InputProps.startAdornment,
                  endAdornment: endAdornment ? (
                    <>
                      {params.InputProps.endAdornment}
                      {endAdornment}
                    </>
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
              />
            )}
          />
        );
      }}
    </SuggestedField>
  );
}
